'use client'

import Link from 'next/link'
import { CategoryIcons } from '../CategoryIcons'
import { useAuth } from '@workos-inc/authkit-nextjs/components'
import { CalendarDays, Info, MapPin, Settings, Settings2, Video } from 'lucide-react'
import { motion } from 'motion/react'

import { RichText } from '@payloadcms/richtext-lexical/react'

import { AttendeeCount } from '@/components/events/AttendeeCount'
import { Media } from '@/components/Media'
import { Button } from '@/components/ui/button'
import { useEventsFormProviderContext } from '@/providers/EventsFormProvider'
import { eventCoverImage } from '@/utilities/events/eventCoverImage'
import { formatDateTime } from '@/utilities/global/formatDateTime'
import { plainTextToRichText } from '@/utilities/plainTextToRichText'
import { cn } from '@/utilities/ui'

import { ClassificationBadge } from './components/ClassificationBadge'
import { StatusIcons } from './components/StatusIcons'

import type { Guest, Topic, User } from '@/payload-types'
import type { DisplayEvent } from '@/utilities/events/displayEvent'

interface EventCardProps {
  event: DisplayEvent
  className?: string
  handleSave?: (eventId: number) => void
  showClassificationBadge?: boolean
  user?: User | null
}

const INNER_ELEMENT_HOVER_CLASSES =
  'group-hover/articles:opacity-50 touchdevice:group-hover/articles:opacity-100 group-hover/post-card:!opacity-100 transition-all duration-200'

export const EventCard: React.FC<EventCardProps> = ({
  event,
  className,
  showClassificationBadge = true,
  user,
}) => {
  const { user: sessionUser } = useAuth()
  const { setEditEventId } = useEventsFormProviderContext()
  const general = event.general
  const coverImage = eventCoverImage(event)
  const cityState = [event.location?.city, event.location?.state].filter(Boolean).join(', ')
  const locationLabel = cityState || event.location?.address1
  const sessionEmail = user?.email ?? sessionUser?.email
  const isHost = event.host?.some((host) => {
    if (typeof host === 'number') return host === user?.id
    if (user && host.id === user.id) return true
    if (sessionUser?.id && host.workosId === sessionUser.id) return true
    if (sessionEmail && host.email === sessionEmail) return true
    return false
  })
  const canManage =
    isHost && Boolean(event.lumaId?.startsWith('evt-')) && event.eventStatus === 'approved'
  const isAttending = event.relatedGuests?.docs?.some(
    (guest) =>
      (guest as Guest).userEmail === sessionEmail && (guest as Guest).approvalStatus === 'approved',
  )
  const isWaitlisted = event.relatedGuests?.docs?.some(
    (guest) =>
      (guest as Guest).userEmail === sessionEmail && (guest as Guest).approvalStatus === 'waitlist',
  )
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={cn(
        'post-card group/post-card @container/event-card relative flex w-full max-w-full flex-col rounded-2xl border border-border bg-white',
        className,
      )}
    >
      <div className="relative z-10 flex flex-1 flex-col @event-xs/event-card:flex-row">
        <div className="block aspect-square w-full overflow-hidden @event-xs/event-card:max-w-25 @event-xs/event-card:min-w-25 @event-xs/event-card:p-4 @event-sm/event-card:max-w-50 @event-sm/event-card:min-w-50 @event-sm/event-card:p-0 @event-md/event-card:max-w-75 @event-md/event-card:min-w-75">
          {coverImage != null ? (
            <Link href={`/events/${event.slug}`} aria-label={`Visit event: ${event.name}`}>
              <Media
                resource={coverImage}
                context={'thumbnail'}
                className="relative aspect-square h-full min-w-full overflow-hidden rounded-t-2xl @event-xs/event-card:rounded-2xl @event-sm/event-card:rounded-l-2xl @event-sm/event-card:rounded-r-none"
                htmlelement="div"
                width={300}
                height={300}
                loading="eager"
              />
            </Link>
          ) : (
            <div className="relative aspect-8/5 w-full bg-muted" />
          )}
        </div>
        <div className="flex flex-col gap-3 p-4 @event-xs/event-card:grow @event-md/event-card:p-6 @event-xl/event-card:px-6.5">
          <div
            className={cn(
              'flex w-full flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between',
              INNER_ELEMENT_HOVER_CLASSES,
            )}
          >
            <div className="flex w-full items-center justify-between gap-2 sm:justify-start sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-4">
                <span className="flex items-center gap-1 font-body text-sm font-medium text-neutral-500">
                  <CalendarDays size={16} />
                  <span className="sr-only">Published on</span>
                  {formatDateTime(general.startAt)}
                </span>
                {event.virtual ? (
                  <Video size={16} />
                ) : (
                  locationLabel && (
                    <div className="flex items-center gap-1 font-body text-sm font-medium text-neutral-500">
                      <MapPin size={16} />
                      <span className="sr-only">Located in</span>
                      {locationLabel}
                    </div>
                  )
                )}
                {event.official === 'unofficial' && <AttendeeCount event={event} />}
              </div>
              <CategoryIcons
                topics={(general.topics ?? []) as Topic[]}
                key={event.id}
                className="flex sm:hidden"
              />
            </div>
            <div className="flex items-center gap-2">
              <StatusIcons
                event={event}
                isAttending={isAttending ?? false}
                isHost={isHost ?? false}
                isWaitlisted={isWaitlisted ?? false}
              />
              {showClassificationBadge && <ClassificationBadge classification={event.official} />}
            </div>
          </div>
          <h3
            className={cn(
              'line-clamp-3 overflow-hidden font-body text-lg leading-tight font-semibold text-ellipsis text-foreground @event-lg/event-card:text-xl @event-xl/event-card:text-2xl',
              INNER_ELEMENT_HOVER_CLASSES,
            )}
          >
            <Link href={`/events/${event.slug}`}>{event.name}</Link>
          </h3>
          <RichText
            data={general.description ?? plainTextToRichText('')}
            className={cn(
              'line-clamp-3 overflow-hidden font-body text-base text-ellipsis text-card-foreground @event-lg/event-card:text-lg',
              INNER_ELEMENT_HOVER_CLASSES,
            )}
          />
          <div
            className={cn(
              'mt-auto flex justify-end gap-2 sm:flex-row sm:justify-between',
              INNER_ELEMENT_HOVER_CLASSES,
            )}
          >
            <div className="mt-4 flex items-center gap-2 sm:mt-0 sm:gap-4">
              <Link href={`/events/${event.slug}`}>
                <Button
                  variant="accent"
                  // customIcon={isAttending || isWaitlisted ? <ArrowRight /> : <Check />}
                  customIcon={<Info />}
                >
                  Details
                </Button>
              </Link>
              {isHost && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    customIcon={<Settings size={10} />}
                    onClick={() => setEditEventId(event.id.toString())}
                  >
                    Edit
                  </Button>
                </div>
              )}
              {canManage && (
                <div className="flex justify-end">
                  <Button variant="outline" customIcon={<Settings2 size={10} />} asChild>
                    <Link href={`/events/${event.slug}/admin`}>Manage</Link>
                  </Button>
                </div>
              )}
            </div>
            <CategoryIcons
              topics={(general.topics ?? []) as Topic[]}
              key={event.id}
              className="hidden sm:flex"
            />
          </div>
        </div>
      </div>
    </motion.article>
  )
}
